import { timeEntryHours } from './time-entry-hours'

/** Keep sub-minute work and persisted rounding when a timer is allocated. */
export function timeEntryMinutes(
  entry: Parameters<typeof timeEntryHours>[0],
): number {
  const duration = entry.duration == null ? null : Number(entry.duration)
  return Math.round(timeEntryHours({ ...entry, duration }) * 60 * 1e6) / 1e6
}

/** Divide minutes at display/input precision, keeping the remainder in the last row. */
export function distributeTimeEntryMinutes(
  total: number,
  weights: readonly number[],
): number[] {
  if (
    !Number.isFinite(total) ||
    total < 0 ||
    weights.some(weight => !Number.isFinite(weight) || weight < 0)
  )
    throw new RangeError('Invalid time allocation')
  const weightTotal = weights.reduce((sum, weight) => sum + weight, 0)
  if (!Number.isFinite(weightTotal))
    throw new RangeError('Invalid time allocation')
  if (!weightTotal) return weights.map(() => 0)
  const totalUnits = Math.round(total * 1e6)
  if (!Number.isSafeInteger(totalUnits))
    throw new RangeError('Time allocation is out of range')
  let cumulativeWeight = 0
  let assignedUnits = 0
  return weights.map((weight, index) => {
    cumulativeWeight += weight
    const throughUnits =
      index === weights.length - 1
        ? totalUnits
        : Math.round(totalUnits * (cumulativeWeight / weightTotal))
    const units = throughUnits - assignedUnits
    assignedUnits = throughUnits
    return units / 1e6
  })
}

/** Human-readable summaries must not turn a positive sub-minute allocation into zero. */
export function formatTimeEntryMinutes(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes <= 0) return '0m'
  const totalSeconds = Math.round(minutes * 60)
  if (!totalSeconds) return '<1s'
  const hours = Math.floor(totalSeconds / 3600)
  const mins = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  return [
    hours ? `${hours}h` : '',
    mins ? `${mins}m` : '',
    seconds ? `${seconds}s` : '',
  ]
    .filter(Boolean)
    .join(' ')
}
